import React from 'react';
import { TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { hitSlop } from '@/constants';
import { STRINGS } from '@/constants/strings';
import {
  AuthBackButton,
  AuthBrandLogo,
  AuthPrimaryButton,
  AuthScreenBackground,
  AuthTextField,
} from '@/features/auth/components';
import { useForgotPassword } from '@/features/auth/hooks/use-forgot-password';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

export function ForgotPasswordV1Screen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  const { email, onChangeEmail, onBlurEmail, handleSendResetEmail, handleCancel, errors } =
    useForgotPassword();

  const strings = STRINGS.auth.forgotPasswordV1;

  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  return (
    <AuthScreenBackground scrollable>
      {/* Back */}
      <Stack width={isTablet ? '100%' : scale(335)}>
        <Stack
          left={isTablet ? scale(50) : undefined}
          paddingTop={top}
          style={isTablet ? styles.backButtonTabletContainer : styles.backButtonContainer}>
          <AuthBackButton onPress={handleCancel} />
        </Stack>
      </Stack>
      <Stack
        flex={1}
        width={scale({ phone: 335, tablet: 380 })}
        style={{
          paddingTop: isTablet ? top + scale(18) : undefined,
          paddingBottom: insets.bottom + scale(12),
        }}>
        {/* Logo */}
        <Stack align="center" marginTop={scale({ phone: 4, tablet: 0 })}>
          <AuthBrandLogo portrait width={scale(154)} height={scale(84)} />
        </Stack>

        {/* Title */}
        <Text
          variant={
            isTablet
              ? 'Font Guardian App/Heading/Heading H5 24px'
              : 'Font Guardian App/Heading/Heading H6 18px'
          }
          style={styles.title}
          accessibilityRole="header">
          {strings.title}
        </Text>

        {/* Description */}
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.description}>
          {strings.description}
        </Text>

        {/* Email */}
        <Stack marginTop={scale({ phone: 40, tablet: 60 })}>
          <AuthTextField
            label={strings.labelEmail}
            iconName="IcSms"
            value={email}
            required
            onChangeText={onChangeEmail}
            onBlur={onBlurEmail}
            error={errors.email?.message}
            placeholder={strings.placeholderEmail}
            fieldProps={{
              keyboardType: 'email-address',
              autoComplete: 'email',
              textContentType: 'emailAddress',
              trimOnBlur: true,
              maxLength: 100,
            }}
          />
        </Stack>

        {/* Flexible spacer pushes the actions to the bottom */}
        <Stack height={scale(24)} />

        {/* Continue */}
        <AuthPrimaryButton onPress={handleSendResetEmail}>
          {strings.buttonContinue}
        </AuthPrimaryButton>

        {/* Back to Login */}
        <TouchableOpacity
          onPress={handleCancel}
          hitSlop={hitSlop}
          style={styles.backToLoginWrapper}>
          <Text variant="Font Guardian App/Body/Body 16 Semibold" style={styles.backToLoginText}>
            {strings.backToLogin}
          </Text>
        </TouchableOpacity>
      </Stack>
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  backButtonContainer: {
    alignSelf: 'flex-start',
  },
  backButtonTabletContainer: {
    position: 'absolute',
  },
  title: {
    marginTop: scale({ phone: 32, tablet: 48 }),
    color: colors['10'],
  },
  description: {
    marginTop: scale(8),
    color: colors['10'],
  },
  backToLoginWrapper: {
    alignSelf: 'center',
    marginTop: scale({ phone: 24, tablet: 30 }),
  },
  backToLoginText: {
    color: colors['10'],
  },
}));

export default ForgotPasswordV1Screen;
